// src/pages/oura-join/index.tsx
//
// /oura/join?token=…: where a merchant's colleague lands from the Oura
// invitation. Three short steps on one card (name and password, the emailed
// code, done) in place of the recruiter sign-up the /verify/team link opens,
// which asks an invited person for a job title and company details they do
// not have. Same token, same calls (see @oura/team/teamApi).

import { useState, type FormEvent } from "react";
import { CheckCircle2Icon } from "lucide-react";
import { cn } from "@oura/lib/cn";
import { PAGE_GROUND, PillButton, SURFACE, TYPE } from "@oura/ui";
import { AccessField } from "@oura/merchant/AccessDrawer";
import { OURA_ROOT } from "@oura/nav";
import { useQuery } from "@util/useQuery";
import ouraLogo from "@asset/oura_logo.png";
import { confirmJoinCode, createJoinLogin, readInviteToken, resendJoinCode, SAMPLE_INVITE, JoinRefused } from "@oura/team/teamApi";

type Step = "account" | "code" | "done";

/** Same rule the password reset uses: long enough to be worth having. */
const MIN_PASSWORD = 8;

function PasswordField({ id, label, value, onChange, error, autoComplete }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string; autoComplete: string }) {
    const errorId = `${id}-error`;
    return (
        <div>
            <label htmlFor={id} className={cn(TYPE.lab, "mb-1 block")}>
                {label}
            </label>
            <input
                id={id}
                type="password"
                value={value}
                autoComplete={autoComplete}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(e) => onChange(e.target.value)}
                className={cn(
                    "h-10 w-full rounded-xl bg-white px-3 text-base text-gray-900 ring-1 ring-inset sm:text-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                    error ? "ring-rose-600" : "ring-black/[0.08]",
                )}
            />
            {error && (
                <p id={errorId} className={cn(TYPE.tc, "m-0 mt-1 text-rose-600")}>
                    {error}
                </p>
            )}
        </div>
    );
}

export default function OuraJoinPage() {
    const query = useQuery();
    const invite = readInviteToken(query.get("token") || "") ?? SAMPLE_INVITE;
    const [step, setStep] = useState<Step>("account");
    const [first, setFirst] = useState("");
    const [last, setLast] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [refusal, setRefusal] = useState("");
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState("");
    const [resent, setResent] = useState(false);

    const business = invite?.businessName || "your team";

    const submitAccount = async (e: FormEvent) => {
        e.preventDefault();
        if (!invite) return;
        const next: Record<string, string> = {};
        if (!first.trim()) next.first = "Enter your first name.";
        if (!last.trim()) next.last = "Enter your last name.";
        if (password.length < MIN_PASSWORD) next.password = `Use at least ${MIN_PASSWORD} characters.`;
        setErrors(next);
        setRefusal("");
        if (Object.keys(next).length > 0) return;
        setBusy(true);
        try {
            await createJoinLogin({ email: invite.email, firstName: first.trim(), lastName: last.trim(), password });
            setStep("code");
        } catch (err) {
            setRefusal(err instanceof JoinRefused ? err.message : "Something went wrong. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const submitCode = async (e: FormEvent) => {
        e.preventDefault();
        if (!invite) return;
        if (!/^\d{6}$/.test(code)) {
            setErrors({ code: "Enter the 6 digits from the email." });
            return;
        }
        setErrors({});
        setRefusal("");
        setBusy(true);
        try {
            await confirmJoinCode(invite.email, code);
            setStep("done");
        } catch (err) {
            setRefusal(err instanceof JoinRefused ? err.message : "Something went wrong. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (!invite) return;
        setResent(false);
        try {
            await resendJoinCode(invite.email);
            setResent(true);
        } catch {
            setRefusal("The code didn't send. Try again in a minute.");
        }
    };

    return (
        <div className={cn("flex min-h-screen flex-col items-center px-4 py-10 sm:py-16", PAGE_GROUND)}>
            <img src={ouraLogo} alt="Oura" className="mb-8 h-7 w-auto" />
            <section className={cn(SURFACE.card, "w-full max-w-md p-6 sm:p-8")}>
                {!invite ? (
                    <>
                        <h1 className={cn(TYPE.tt, "m-0 text-gray-900")}>This invite link doesn't work</h1>
                        <p className={cn(TYPE.tb, "m-0 mt-2 text-gray-600")}>
                            Part of the link may be missing. Open the email again and tap the button, or ask your colleague to send a new invite.
                        </p>
                    </>
                ) : step === "account" ? (
                    <>
                        <h1 className="m-0 text-2xl font-semibold tracking-[-0.01em] text-gray-900">Join {business} on Oura</h1>
                        <p className={cn(TYPE.tb, "m-0 mt-2 text-gray-600")}>
                            {invite.inviterName ? `${invite.inviterName} invited you. ` : ""}Set up your login to see how {business} shows up on Google and AI assistants.
                        </p>
                        <form onSubmit={submitAccount} noValidate className="mt-6 flex flex-col gap-4">
                            <div>
                                <span className={cn(TYPE.lab, "mb-1 block")}>Email</span>
                                <p className="m-0 flex h-10 items-center rounded-xl bg-gray-50 px-3 text-sm text-gray-700 ring-1 ring-inset ring-black/[0.06]">{invite.email}</p>
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <AccessField id="join-first" label="First name" autoComplete="given-name" value={first} onChange={setFirst} error={errors.first} />
                                <AccessField id="join-last" label="Last name" autoComplete="family-name" value={last} onChange={setLast} error={errors.last} />
                            </div>
                            <PasswordField id="join-password" label="Choose a password" autoComplete="new-password" value={password} onChange={setPassword} error={errors.password} />
                            {refusal && (
                                <p role="alert" className={cn(TYPE.tb, "m-0 rounded-xl bg-rose-50 px-3 py-2 text-rose-700")}>
                                    {refusal}
                                </p>
                            )}
                            <PillButton type="submit" variant="primary" busy={busy} className="h-10 w-full">
                                Continue
                            </PillButton>
                        </form>
                    </>
                ) : step === "code" ? (
                    <>
                        <h1 className="m-0 text-2xl font-semibold tracking-[-0.01em] text-gray-900">Check your email</h1>
                        <p className={cn(TYPE.tb, "m-0 mt-2 text-gray-600")}>
                            We sent a 6-digit code to <span className="font-medium text-gray-900">{invite.email}</span>. Enter it to finish.
                        </p>
                        <form onSubmit={submitCode} noValidate className="mt-6 flex flex-col gap-4">
                            <div>
                                <label htmlFor="join-code" className={cn(TYPE.lab, "mb-1 block")}>
                                    Code
                                </label>
                                <input
                                    id="join-code"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    aria-invalid={errors.code ? true : undefined}
                                    className={cn(
                                        "h-12 w-full rounded-xl bg-white px-3 text-center text-xl tracking-[0.5em] text-gray-900 ring-1 ring-inset tabular-nums",
                                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                                        errors.code ? "ring-rose-600" : "ring-black/[0.08]",
                                    )}
                                />
                                {errors.code && <p className={cn(TYPE.tc, "m-0 mt-1 text-rose-600")}>{errors.code}</p>}
                            </div>
                            {refusal && (
                                <p role="alert" className={cn(TYPE.tb, "m-0 rounded-xl bg-rose-50 px-3 py-2 text-rose-700")}>
                                    {refusal}
                                </p>
                            )}
                            <PillButton type="submit" variant="primary" busy={busy} className="h-10 w-full">
                                Finish
                            </PillButton>
                            <p className={cn(TYPE.tc, "m-0 text-center text-gray-500")}>
                                {resent ? "Sent again. It can take a minute." : "No email? Check spam, or "}
                                {!resent && (
                                    <button type="button" onClick={resend} className="font-medium text-blue-600 hover:underline">
                                        send the code again
                                    </button>
                                )}
                            </p>
                        </form>
                    </>
                ) : (
                    <div className="text-center">
                        <CheckCircle2Icon aria-hidden className="mx-auto h-10 w-10 text-green-600" />
                        <h1 className="m-0 mt-3 text-2xl font-semibold tracking-[-0.01em] text-gray-900">You're in</h1>
                        <p className={cn(TYPE.tb, "m-0 mt-2 text-gray-600")}>
                            You can now sign in to {business}'s Oura account with {invite.email}.
                        </p>
                        <PillButton
                            variant="primary"
                            className="mt-6 h-10 w-full"
                            onClick={() => {
                                window.location.href = `${OURA_ROOT}/dashboard`;
                            }}
                        >
                            Open the dashboard
                        </PillButton>
                    </div>
                )}
            </section>
        </div>
    );
}
